// ── Standalone app components (auth-aware where noted) ──
import { useState, useEffect, useCallback, useRef } from "react";
import logo from "../logo.png";
import { B } from "./theme";
import { org } from "./org";
import { useAuth } from "./api";
import { now } from "./format";
import { Spinner, Card, Th, Td, Btn, RoleBadge } from "./ui";

// ─── LOGIN PAGE ───────────────────────────────────────────────
// Cloudflare Turnstile public site key. Set REACT_APP_TURNSTILE_SITEKEY at build time
// (or paste the key here). Leave empty to disable the widget entirely.
const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || "0x4AAAAAAENJDmXgL0xL6zjM";

export const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tsToken, setTsToken] = useState("");           // Turnstile token for the next submit
  const tsRef = useRef(null);
  const tsWidgetId = useRef(null);
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  // ── Second step: the emailed code (api/login answers mfaRequired) ──
  // Trusted-browser tokens are kept per username, so two people sharing a
  // machine each need their own. They only skip the code, never the password.
  const [mfa, setMfa] = useState(null);           // { challengeId, sentTo }
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [note, setNote] = useState(null);
  const DEVICES = "ls_trusted_devices";
  const readDevices = () => { try { return JSON.parse(localStorage.getItem(DEVICES) || "{}") || {}; } catch (e) { return {}; } };
  const deviceFor = (u) => readDevices()[String(u || "").trim().toLowerCase()] || null;
  const saveDevice = (u, t) => {
    try { const d = readDevices(); d[String(u || "").trim().toLowerCase()] = t; localStorage.setItem(DEVICES, JSON.stringify(d)); } catch (e) {}
  };
  const forgetDevice = (u) => {
    try { const d = readDevices(); delete d[String(u || "").trim().toLowerCase()]; localStorage.setItem(DEVICES, JSON.stringify(d)); } catch (e) {}
  };
  const finish = (data) => {
    localStorage.setItem("ls_token", data.token);
    localStorage.setItem("ls_user", JSON.stringify(data.user));
    onLogin(data.user, data.token);
  };
  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) { setError("Enter the 6-digit code from the email."); return; }
    setLoading(true); setError(null); setNote(null);
    try {
      const res = await fetch("/api/loginVerify", { method:"POST", headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ challengeId: mfa.challengeId, code: code.trim(), trustDevice: trust }) });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "That code did not work.");
        setLoading(false);
        if (res.status === 429 || (res.status === 400 && !data.expired)) { setMfa(null); setCode(""); resetTurnstile(); }
        return;
      }
      if (data.deviceToken) saveDevice(username, data.deviceToken);
      finish(data);
    } catch (err) { setError("Verification failed. Please try again."); setLoading(false); }
  };
  const resend = async () => {
    setError(null); setNote(null);
    try {
      const res = await fetch("/api/loginVerify", { method:"POST", headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ challengeId: mfa.challengeId, action: "resend" }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not send another code."); return; }
      setNote(`A new code is on its way to ${data.sentTo}.`); setCode("");
    } catch (err) { setError("Could not send another code."); }
  };

  // Load Cloudflare Turnstile and render the widget once (only when a site key is configured).
  useEffect(() => {
    if (!TURNSTILE_SITEKEY) return;
    const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    const renderWidget = () => {
      if (!window.turnstile || !tsRef.current || tsWidgetId.current !== null) return;
      tsWidgetId.current = window.turnstile.render(tsRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        theme: "dark",
        callback: (t) => setTsToken(t),
        "error-callback": () => setTsToken(""),
        "expired-callback": () => setTsToken(""),
      });
    };
    if (window.turnstile) { renderWidget(); }
    else {
      let s = document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile"]');
      if (!s) { s = document.createElement("script"); s.src = SRC; s.async = true; s.defer = true; s.onload = renderWidget; document.head.appendChild(s); }
      else { s.addEventListener("load", renderWidget); }
    }
    return () => { try { if (window.turnstile && tsWidgetId.current !== null) window.turnstile.remove(tsWidgetId.current); } catch (e) {} tsWidgetId.current = null; };
  }, []);

  const resetTurnstile = () => { try { if (window.turnstile && tsWidgetId.current !== null) window.turnstile.reset(tsWidgetId.current); } catch (e) {} setTsToken(""); };

  const handleSQLLogin = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITEKEY && !tsToken) { setError("Please complete the verification below."); return; }
    setLoading(true); setError(null);
    try {
      const res = await fetch("/api/login", { method:"POST", headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({ username, password, turnstileToken: tsToken, deviceToken: deviceFor(username) }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Invalid credentials"); setLoading(false); resetTurnstile(); return; }
      if (data.mfaRequired) {
        // A trusted-browser token that did not work is stale (expired or revoked).
        if (deviceFor(username)) forgetDevice(username);
        setMfa({ challengeId: data.challengeId, sentTo: data.sentTo }); setCode(""); setTrust(false);
        setLoading(false); return;
      }
      finish(data);
    } catch (err) { setError("Login failed. Please try again."); setLoading(false); resetTurnstile(); }
  };

  const handleADLogin = () => {
    if (isLocal) { setError("Microsoft sign-in is only available on the live Azure app."); return; }
    window.location.href = "/.auth/login/aad?post_login_redirect_uri=/";
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"var(--font-sans)", position:"relative", overflow:"hidden", background:"#0a1a24" }}>
    <picture>
      <source type="image/avif" sizes="100vw"
        srcSet="/background-1280.avif 1280w, /background-1920.avif 1920w, /background-2560.avif 2560w, /background-3840.avif 3840w" />
      <source type="image/webp" sizes="100vw"
        srcSet="/background-1280.webp 1280w, /background-1920.webp 1920w, /background-2560.webp 2560w, /background-3840.webp 3840w" />
      <img src="/background-1920.webp" alt="" aria-hidden="true" decoding="async" fetchPriority="high"
        style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", opacity:0.18 }} />
    </picture>

      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at center, rgba(18,181,219,0.04) 0%, rgba(10,26,36,0.7) 100%)" }} />
      <div style={{ position:"relative", zIndex:10, width:360, maxWidth:"92vw", background:"rgba(13,28,38,0.92)", border:"1px solid "+B.cyan, borderRadius:12, padding:"53px 32px 53px", boxShadow:"0 0 40px rgba(18,181,219,0.18), 0 0 0 1px rgba(18,181,219,0.08)" }}>
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <img src={logo} alt={org("name")} style={{ height:40, objectFit:"contain", filter:"brightness(0) invert(1)" }} />
        </div>
        <button onClick={handleADLogin} style={{ width:"100%", padding:"11px 16px", marginBottom:54, background:B.cyan, border:"none", borderRadius:6, color:"#fff", fontSize:14, fontWeight:500, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10, letterSpacing:"0.01em" }}>
          <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
          </svg>
          Sign in with Microsoft
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:44 }}>
          <div style={{ flex:1, height:"0.5px", background:"rgba(18,181,219,0.25)" }} />
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", letterSpacing:"0.05em" }}>OR</span>
          <div style={{ flex:1, height:"0.5px", background:"rgba(18,181,219,0.25)" }} />
        </div>
        {mfa && <form onSubmit={verify}>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.85)", marginBottom:18, lineHeight:1.5 }}>
            We emailed a 6-digit code to <strong>{mfa.sentTo}</strong>. Enter it to finish signing in. It expires in 10 minutes.
          </div>
          <input value={code} onChange={e=>setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} autoFocus
                 inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                 style={{ width:"100%", boxSizing:"border-box", background:"transparent", border:"none", borderBottom:"1px solid rgba(18,181,219,0.6)",
                          padding:"10px 0", fontSize:24, letterSpacing:"0.5em", textAlign:"center", color:"#fff", outline:"none", caretColor:B.cyan, marginBottom:20 }} />
          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12.5, color:"rgba(255,255,255,0.75)", marginBottom:20, cursor:"pointer" }}>
            <input type="checkbox" checked={trust} onChange={e=>setTrust(e.target.checked)} />
            Trust this browser for 30 days (don&apos;t on a shared computer)
          </label>
          {note && <div style={{ fontSize:12, color:B.cyanMid, marginBottom:12 }}>{note}</div>}
          {error && <div style={{ background:"rgba(226,75,74,0.15)", border:"0.5px solid rgba(226,75,74,0.4)", borderRadius:6, padding:"8px 12px", fontSize:12, color:"#f09595", marginBottom:16 }}>{error}</div>}
          <button type="submit" disabled={loading || code.length !== 6} style={{ width:"100%", padding:"12px 16px", background:B.cyan, border:"none", borderRadius:6, color:"#fff", fontSize:14, fontWeight:500, cursor:(loading || code.length !== 6)?"not-allowed":"pointer", opacity:(loading || code.length !== 6)?0.7:1, letterSpacing:"0.08em", textTransform:"uppercase" }}>
            {loading ? "Verifying..." : "Verify"}
          </button>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:16, fontSize:12 }}>
            <span onClick={resend} style={{ color:B.cyanMid, cursor:"pointer" }}>Send a new code</span>
            <span onClick={() => { setMfa(null); setCode(""); setError(null); setNote(null); resetTurnstile(); }} style={{ color:"rgba(255,255,255,0.55)", cursor:"pointer" }}>Back</span>
          </div>
        </form>}
        <form onSubmit={handleSQLLogin} style={{ display: mfa ? "none" : "block" }}>
          <div style={{ marginBottom:40 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:500, color:"rgba(255,255,255,0.85)", marginBottom:26 }}>Username</label>
            <input value={username} onChange={e=>setUsername(e.target.value)} placeholder="Enter your username" style={{ width:"100%", boxSizing:"border-box", background:"transparent", border:"none", borderBottom:"1px solid rgba(18,181,219,0.4)", padding:"8px 0", fontSize:13, color:"#fff", outline:"none", caretColor:B.cyan }} />
          </div>
          <div style={{ marginBottom:48 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:500, color:"rgba(255,255,255,0.85)", marginBottom:6 }}>Password</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Enter your password" style={{ width:"100%", boxSizing:"border-box", background:"transparent", border:"none", borderBottom:"1px solid rgba(18,181,219,0.4)", padding:"8px 0", fontSize:13, color:"#fff", outline:"none", caretColor:B.cyan }} />
          </div>
          {TURNSTILE_SITEKEY && <div ref={tsRef} style={{ marginBottom:24, display:"flex", justifyContent:"center" }} />}
          {error && <div style={{ background:"rgba(226,75,74,0.15)", border:"0.5px solid rgba(226,75,74,0.4)", borderRadius:6, padding:"8px 12px", fontSize:12, color:"#f09595", marginBottom:16 }}>{error}</div>}
          <button type="submit" disabled={loading || (TURNSTILE_SITEKEY && !tsToken)} style={{ width:"100%", padding:"12px 16px", background:B.cyan, border:"none", borderRadius:6, color:"#fff", fontSize:14, fontWeight:500, cursor:(loading || (TURNSTILE_SITEKEY && !tsToken))?"not-allowed":"pointer", opacity:(loading || (TURNSTILE_SITEKEY && !tsToken))?0.7:1, letterSpacing:"0.08em", textTransform:"uppercase" }}>
            {loading ? "Signing in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── NOTIFICATION CENTER ─────────────────────────────────────
export const NotifCenter = ({ notifs, onRead, onReadAll, onDismiss, open, onToggle }) => {
  const unread = notifs.filter(n=>!n.IsRead).length;
  return (
    <div style={{ position:"relative" }}>
      <button onClick={onToggle} style={{ background:"none", border:"0.5px solid rgba(18,181,219,0.3)", borderRadius:6, padding:"5px 10px", cursor:"pointer", color:B.cyanMid, fontSize:13, display:"flex", alignItems:"center", gap:6 }}>
        🔔 {unread>0 && <span style={{ background:"#D85A30", color:"#fff", borderRadius:10, fontSize:11, padding:"1px 6px", fontWeight:500 }}>{unread}</span>}
      </button>
      {open && (
        <div style={{ position:"absolute", right:0, top:"calc(100% + 8px)", width:380, background:"var(--color-background-primary)", borderRadius:"var(--border-radius-lg)", border:"0.5px solid var(--color-border-secondary)", boxShadow:"0 4px 24px rgba(0,0,0,0.12)", zIndex:200 }}>
          <div style={{ padding:"12px 16px", borderBottom:"0.5px solid var(--color-border-tertiary)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontWeight:500, fontSize:14 }}>Notifications</span>
            {unread>0 && <button onClick={onReadAll} style={{ background:"none", border:"none", fontSize:12, color:B.cyan, cursor:"pointer" }}>Mark all read</button>}
          </div>
          <div style={{ maxHeight:340, overflowY:"auto" }}>
            {notifs.length===0 && <div style={{ padding:20, fontSize:13, color:"var(--color-text-secondary)" }}>No notifications.</div>}
            {notifs.map(n=>(
              <div key={n.id} onClick={()=>onRead(n.id)} style={{ padding:"12px 16px", borderBottom:"0.5px solid var(--color-border-tertiary)", cursor:"pointer", background:n.IsRead?"":"var(--color-background-secondary)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:n.IsRead?400:500, marginBottom:2 }}><span style={{ marginRight:6 }}>{n.Type==="Overdue"?"🔴":"🔵"}</span>{n.Title}</div>
                    <div style={{ fontSize:12, color:"var(--color-text-secondary)" }}>{n.Body}</div>
                    <div style={{ fontSize:11, color:"var(--color-text-secondary)", marginTop:4 }}>{n.Ts}</div>
                  </div>
                  <button onClick={e=>{e.stopPropagation();onDismiss(n.id);}} style={{ background:"none", border:"none", fontSize:14, cursor:"pointer", color:"var(--color-text-secondary)", padding:0, lineHeight:1 }}>×</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── DOCUMENT UPLOADER ───────────────────────────────────────
export const DocUploader = ({ parentId, canEdit, canDelete }) => {
  const { api } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();
  const load = useCallback(async()=>{ setLoading(true); try { setDocs(await api.get("getDocuments?parentId="+parentId)); } catch(e){ setDocs([]); } setLoading(false); },[parentId]);
  useEffect(()=>{ load(); },[load]);
  const handleFile = async e => { const file=e.target.files[0]; if(!file) return; await api.post("saveDocument",{ parentId, parentType:"loan", name:file.name, size:(file.size/1024).toFixed(1)+"KB", uploadedAt:now() }); e.target.value=""; load(); };
  const handleDelete = async id => { await api.post("deleteRecord",{ table:"Documents", id }); load(); };
  if(loading) return <Spinner />;
  return (
    <div>
      {canEdit && <div style={{ marginBottom:12 }}><input ref={fileRef} type="file" style={{ display:"none" }} onChange={handleFile} /><Btn onClick={()=>fileRef.current.click()} variant="primary">+ Attach document</Btn><span style={{ fontSize:12, color:"var(--color-text-secondary)", marginLeft:10 }}>PDF, DOCX, XLSX, images accepted</span></div>}
      {docs.length===0 ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"12px 0" }}>No documents attached.</div>
        : <Card><table style={{ width:"100%", borderCollapse:"collapse" }}><thead><tr><Th>File name</Th><Th>Size</Th><Th>Uploaded</Th>{canDelete&&<Th></Th>}</tr></thead>
            <tbody>{docs.map(d=>(<tr key={d.id} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}><Td style={{ fontWeight:500 }}>📄 {d.Name}</Td><Td style={{ color:"var(--color-text-secondary)" }}>{d.Size}</Td><Td style={{ color:"var(--color-text-secondary)" }}>{d.UploadedAt}</Td>{canDelete&&<Td><Btn variant="danger" onClick={()=>handleDelete(d.id)}>Remove</Btn></Td>}</tr>))}</tbody>
          </table></Card>}
    </div>
  );
};

// ─── AUDIT LOG VIEWER ────────────────────────────────────────
export const AuditViewer = ({ recordId }) => {
  const { api } = useAuth();
  const [log, setLog] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(()=>{ const path=recordId?"getAuditLog?recordId="+recordId:"getAuditLog"; api.get(path).then(setLog).catch(()=>setLog([])).finally(()=>setLoading(false)); },[recordId]);
  if(loading) return <Spinner />;
  if(!log.length) return <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"12px 0" }}>No history yet.</div>;
  return <Card><table style={{ width:"100%", borderCollapse:"collapse" }}><thead><tr><Th>Timestamp</Th><Th>User</Th><Th>Role</Th><Th>Action</Th><Th>Detail</Th></tr></thead>
    <tbody>{log.map(e=>(<tr key={e.id} onMouseEnter={ev=>ev.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={ev=>ev.currentTarget.style.background=""}><Td style={{ fontFamily:"var(--font-mono)", fontSize:12, whiteSpace:"nowrap" }}>{e.Ts}</Td><Td style={{ fontWeight:500 }}>{e.UserName}</Td><Td><RoleBadge role={e.Role} /></Td><Td>{e.Action}</Td><Td style={{ color:"var(--color-text-secondary)" }}>{e.Detail}</Td></tr>))}</tbody>
  </table></Card>;
};
